import { z } from "zod";

/**
 * Заметки преподавателя о неточностях. Проект целиком:
 * `docs/feedback-notes-design.md`.
 *
 * Что здесь лежит и почему именно здесь:
 *
 * 1. **Контракт ручки `POST /v1/feedback`.** Как у всего остального, одна схема
 *    на обе стороны.
 * 2. **Маскировка адресов и словари сегментов.** Это не помощник клиента, а
 *    правило приватности, и живёт оно в общем пакете затем, что проверяется с
 *    ДВУХ сторон: веб тестирует саму функцию, а `apps/api` тестом сверяет
 *    словарь путей с манифестом маршрутов, чтобы новый маршрут не приехал без
 *    строки в словаре (раздел 3.2 проекта).
 *
 * Барьер здесь общий (`@edu/shared`): секретов в этих типах нет, а форма нужна
 * обеим сторонам. В бандл ученика это попадает и попадать может: словарь имён
 * маршрутов не секрет, а вот содержимое адресов как раз и есть то, что этот
 * файл из базы убирает.
 */

/* ─────────────────────────── маскировка ─────────────────────────── */

/**
 * ⚠️ **Белый список, а не вычёркивание подозрительного.** Адрес `/join/<код>`
 * содержит код приглашения, а он секрет и даёт доступ к паре: преподаватель,
 * открывший свою же ссылку и написавший оттуда заметку, положил бы живой секрет
 * в таблицу на полгода.
 *
 * Чёрный список («замаскировать то, что похоже на uuid») отвалился бы на первом
 * же не-uuid секрете. Белый на новом секрете не отваливается никогда: он его
 * просто не знает и маскирует.
 *
 * Сверено с литералами `path=` в `apps/web/src/App.tsx`. Проверку держит
 * `feedback.test.ts`, а не просьба: забытый сегмент даёт тихую потерю качества
 * (`/schedule/series/:id` превращается в `/:id/:id/:id`), а лишний кладёт секрет
 * в базу, и обе ошибки невидимы.
 */
export const FEEDBACK_ROUTE_SEGMENTS: readonly string[] = [
  "announcements",
  /* Проверка ответа текстом, `/homework/:id/answer-review` (1.9
     docs/homework-scratch-design.md). Секрета в сегменте нет. */
  "answer-review",
  "archive",
  /* PDF, открытый книгой, `/media/book/:fileId` (docs/pdf-book-view-design.md,
     Р7). Секрета в сегменте нет, идентификатор файла становится `:id`. */
  "book",
  "browse",
  /* Ссылка гостя звонка с преподавателями, `/call/:lessonId`
     (docs/teacher-calls-design.md, 5.1). Ключ ссылки стоит во фрагменте
     адреса, как у `/teacher-invite`, и в заметку не попадает: маскировка
     берёт `pathname`, а якорь и без того отрезается в `segments()` ниже.
     Идентификатор звонка становится `:id`. */
  "call",
  /*
   * Предполётная проверка звука, `/check`. Без строки здесь маскировка
   * превратила бы адрес в `/:id`, и `route-dictionary.test.ts` это поймает.
   * Секрета в адресе нет: значение `?next=` в заметку не попадает вовсе,
   * строка запроса отрезается в `segments()` ниже.
   */
  "check",
  "coverage",
  /* Карточки (docs/flashcards-design.md): `/decks/:id`, `/flashcards`,
     `/flashcards/train`, `/flashcards/:id/browse` (сегмент `browse` выше),
     `/decks/:id/preview` (сегмент `preview` ниже) */
  "decks",
  "flashcards",
  "groups",
  "homework",
  "import",
  "join",
  "lesson",
  "lessons",
  /* Ссылки и файлы, присланные ученику, `/materials`
     (docs/material-receipt-design.md, шаг 3). Идентификаторов в адресе нет. */
  "materials",
  "media",
  "new",
  "plans",
  /* Просмотр колоды преподавателем, `/decks/:id/preview` (8
     docs/flashcard-card-design.md). Секрета в сегменте нет. */
  "preview",
  "review",
  "schedule",
  "series",
  "students",
  /* Приглашение преподавателя, `/teacher-invite` (раздел 9
     docs/admin-panel-design.md). Код стоит во фрагменте адреса, как у `/view`,
     и в заметку не попадает: `pathname` фрагмента не несёт. */
  "teacher-invite",
  "train",
  /* Экран озвучки, `/media/tts`. Идентификаторов в адресе у него нет, но без
     строки здесь маскировка превратила бы сегмент в `/:id`. */
  "tts",
  /* Вход в просмотр кабинета админом, `/view`. Код стоит во фрагменте адреса,
     а фрагмент в заметку не попадает: `window.location.pathname` его не несёт. */
  "view",
  "whats-new",
];

/**
 * Словарь сегментов путей API, для буфера последних отказов (раздел 6.4
 * проекта). Собран из манифеста маршрутов `apps/api/src/app.test.ts`.
 *
 * ⚠️ Здесь есть `invites`, и именно поэтому словарь нужен: путь погашения ссылки
 * это `/v1/invites/<код>/redeem`, то есть отказ на нём положил бы код
 * приглашения в базу ровно так же, как адрес `/join/<код>`.
 *
 * ⚠️ **Новый маршрут API приезжает со строкой в этом списке.** Это проверяется
 * тестом `apps/api/src/feedback-paths.test.ts`, который сверяет список с
 * манифестом и падает на незнакомом сегменте.
 */
export const FEEDBACK_API_SEGMENTS: readonly string[] = [
  "admin", "ai", "announcements", "appends",
  /* Карточки (docs/flashcards-design.md, 9.1) */
  "assignments", "browsed", "cards", "decks", "flashcards", "today", "words",
  /* Ответ ученика текстом к домашке (1.8 docs/homework-scratch-design.md) */
  "answer",
  "archive", "attempts", "auth", "batch", "block", "board",
  "board-image",
  /* Материалы на доску урока (docs/room-material-insert-design.md, раздел 4):
     право на файлы и задания комнаты */
  "board-files", "material-jobs",
  "board-name",
  "board-ticket",
  /* Самозапись на открытое время (docs/booking-design.md) */
  "book", "booking", "openings",
  "callback", "cancel", "change", "check", "complete",
  /* Звонок с преподавателями (docs/teacher-calls-design.md, 8.1): создание,
     ссылка гостя и «убрать из звонка». Ключ ссылки едет телом, а не путём, и
     в буфер отказов не попадает */
  "calls", "guest-link", "guests",
  "compose",
  "contacts",
  "conversations",
  "dialogue",
  "disband",
  "changelog-seen",
  /* «Позвать коллегу» (docs/colleague-referral-design.md, 3.6): ручка кабинета
     и три ручки панели `/admin/v1/referrals*` */
  "colleague-referrals", "referrals", "summary", "dismiss",
  "content", "course-test", "coverage",
  /* Обложка PDF на плитке «Материалов» (docs/pdf-book-view-design.md, Р1) */
  "cover",
  "credits",
  "diagnostic-sheet", "done", "duplicate", "end", "entry", "events",
  "exception",
  "exercise-batch", "exercise-keys", "exercises", "external-call-url", "feedback", "files",
  "fingerprint", "folders",
  "generator-topics", "google", "groups", "healthz", "homework",
  /* Домашка без заготовки (docs/homework-scratch-design.md): ленты картинок урока
     и заготовки, загрузка картинки в форме выдачи, картинки домашки заготовкой */
  "images", "material-image", "save-as-plan",
  /* Текст с картинки (`docs/handwriting-recognition-design.md`, решение 20):
     чтение кадра и правка человека, оба под `/v1/image-text/files/:fileId`. */
  "image-text",
  /* Задания из PDF (`docs/exercise-extraction-design.md`, раздел 9) */
  "extractions", "latest", "retry",
  /* Подтверждение переноса заглушки: `/v1/invites/:code/adopt`
     (docs/account-adopt-design.md, 8.2) */
  "adopt",
  /* Список переносов в панели: `/admin/v1/adopts` */
  "adopts",
  "homework-batch", "import", "inventory", "invites", "jobs", "keys", "lesson",
  "lessons", "link", "login", "logout", "logout-all", "materials", "me", "media", "members",
  "messages",
  "move",
  "mic",
  "network", "next-occurrence", "occurrence-chats", "occurrences", "oge",
  /* Отметка и веха выданного материала (docs/material-receipt-design.md, шаги 3-4) */
  "opened", "progress",
  /* Отметка нажатия на ссылку текста задания: `/v1/homework/:homeworkId/links/opened`
     (Р9 docs/homework-media-plan.md) */
  "links",
  "pages", "participants", "password",
  "marks", "months", "payload",
  "photo", "photos",
  "plan", "plans", "plots", "preflight", "preview", "profile", "purge", "questions", "quota",
  "read", "readyz",
  "recent",
  "redeem", "reissue-invite", "rename", "replies", "restore", "return", "reveal", "review",
  "reviews", "revoke", "revoke-all",
  "schedule", "seen", "series", "sessions", "settle",
  "signal", "signals",
  "skills", "slides", "slots", "start", "stats",
  "student-link",
  "students",
  "subject", "subjects",
  "sync", "telemetry", "thumb", "time-zone", "time-zone-drift",
  "tracking",
  "token", "transcript", "trash", "tts", "unblock", "unopened", "unread", "uploads", "usage", "users", "v1",
  "view",
  "viewed",
  "voices",
  "void",
  "warnings",
  "webrtc",
];

/** Путь без строки запроса и якоря, разбитый на непустые сегменты. */
function segments(pathname: string): string[] {
  const bare = pathname.split("?", 1)[0] ?? "";
  const path = bare.split("#", 1)[0] ?? "";
  return path.split("/").filter((p) => p.length > 0);
}

function mask(pathname: string, known: readonly string[]): string {
  const dictionary = new Set(known);
  const parts = segments(pathname).map((p) => (dictionary.has(p) ? p : ":id"));
  // ⚠️ Пустой результат это «/», а не пустая строка: маршруты `/` и `*`
  // существуют, а схема требует route длиной не меньше единицы. Без этой
  // строки заметка с корня получала бы invalid_body, то есть постоянную потерю
  return parts.length === 0 ? "/" : `/${parts.join("/")}`;
}

/** Адрес экрана приложения без единого идентификатора и без единого секрета. */
export function maskRoute(pathname: string): string {
  return mask(pathname, FEEDBACK_ROUTE_SEGMENTS);
}

/** Путь запроса к API, по тем же правилам и по своему словарю. */
export function maskApiPath(path: string): string {
  return mask(path, FEEDBACK_API_SEGMENTS);
}

/**
 * Идентификаторы предмета разговора из адреса.
 *
 * ⚠️ Берутся разбором пути, а НЕ через `useParams()`: виджет монтируется рядом
 * с набором маршрутов, а не внутри элемента маршрута, и `useParams` вернул бы
 * там пустой объект всегда, молча (раздел 3.3.2 проекта).
 *
 * ⚠️ **Сегмента `join` в таблице нет и не будет.** За ним стоит секрет, а не
 * идентификатор. Это единственное исключение, и оно записано здесь, чтобы не
 * выводиться заново каждым, кто добавляет сюда четвёртую строку.
 */
export function idsFromRoute(pathname: string): {
  lessonId?: string;
  planId?: string;
  homeworkId?: string;
} {
  const parts = segments(pathname);
  const out: { lessonId?: string; planId?: string; homeworkId?: string } = {};
  for (let i = 0; i + 1 < parts.length; i += 1) {
    const key = parts[i];
    const value = parts[i + 1];
    if (value === undefined || !UUID_RE.test(value)) continue;
    if (key === "lesson" || key === "archive") out.lessonId = value;
    if (key === "plans") out.planId = value;
    if (key === "homework") out.homeworkId = value;
  }
  return out;
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/* ──────────────────────────── контракт ──────────────────────────── */

/**
 * Строка без управляющих символов.
 *
 * ⚠️ Это не гигиена, а защита от вечного ретрая. PostgreSQL не хранит нулевой
 * байт ни в `text`, ни внутри `jsonb`, и вставка падает ошибкой драйвера, то
 * есть пятисоткой. А пятисотка по классификации очереди это ВРЕМЕННАЯ неудача,
 * значит одна заметка с битым символом будет ретраиться каждые пятнадцать минут
 * тридцать суток подряд и всё это время тратить общую квоту лимита частоты
 * (раздел 5.3 проекта). Управляющие символы приезжают не только злым умыслом:
 * копипаста из PDF даёт их регулярно.
 *
 * ⚠️ **Экспортируется затем, чтобы правило было одно.** Живёт здесь по истории
 * (первым его понадобился этот файл), а не потому, что относится к заметкам:
 * ровно та же пятисотка ждёт ЛЮБОЕ поле со свободным текстом от человека.
 * Сегодня отсюда его берут `./announcements.ts` (тело объявления и ответа) и
 * `./groups.ts` (название группы и имя участника внутри неё), и берут именно
 * отсюда, а не пишут второе регулярное выражение: второе однажды отстанет от
 * первого.
 */
export const Printable = z
  .string()
  .refine((s) => !/[\x00-\x08\x0b\x0c\x0e-\x1f\x7f]/.test(s), "control characters");

export const FeedbackContext = z.object({
  /** Версия бандла. Впекается через `define`, см. 3.3.1 проекта. */
  build: z.string().max(40).optional(),
  viewport: z
    .object({
      w: z.number().int().min(0).max(100_000),
      h: z.number().int().min(0).max(100_000),
      dpr: z.number().min(0).max(16),
    })
    .optional(),
  online: z.boolean().optional(),
  /** Только Chromium. Отсутствие это `null`, а не догадка. */
  effectiveType: z.string().max(16).nullish(),
  downlink: z.number().min(0).max(100_000).nullish(),
  lessonId: z.string().uuid().optional(),
  planId: z.string().uuid().optional(),
  homeworkId: z.string().uuid().optional(),
  role: z.enum(["teacher", "student"]).optional(),
  /**
   * В каком режиме шёл урок. Два факта, которые в комнате ДЕЙСТВИТЕЛЬНО есть,
   * вместо вычисляемого «уровня лестницы деградации»: такого понятия в коде
   * нет, есть только комментарии о нём (раздел 3.3.3 проекта).
   */
  video: z.boolean().optional(),
  noVideoReason: Printable.max(64).optional(),
  /**
   * ⚠️ Счётчики, и размера документа в байтах среди них нет намеренно: узнать
   * его можно только через `encodeStateAsUpdate()`, а это операция с пиком
   * около 15 ГБ на доске «нарисовал, стёр» (раздел 4 CLAUDE.md). Виджет мерил
   * бы доску способом, роняющим вкладку ровно там, где мерка и интересна.
   */
  boardElements: z.number().int().min(0).max(1_000_000).optional(),
  slides: z.number().int().min(0).max(10_000).optional(),
  fails: z
    .array(
      z.object({
        code: Printable.max(48),
        status: z.number().int().min(0).max(999),
        /** Уже замаскированный путь. Сырых путей контракт не принимает. */
        path: Printable.max(120),
        ago: z.number().int().min(0).max(2_592_000_000),
      }),
    )
    .max(3)
    .optional(),
});
export type FeedbackContext = z.infer<typeof FeedbackContext>;

/**
 * ⚠️ Схема контекста намеренно НЕ `.strict()`. Незнакомое поле zod по умолчанию
 * отбрасывает, и это единственное верное поведение: новый бандл на старом API
 * (обычное состояние в окне выкатки) пришлёт поле, которого API не знает.
 * `.strict()` ответил бы `invalid_body`, то есть потерял бы заметку из-за поля,
 * без которого заметка полна.
 *
 * ⚠️ Все поля необязательные, включая те, что есть всегда. Снимок собирается из
 * браузерных API, половина которых где-нибудь отсутствует. Обязательное поле
 * означало бы, что сборщик снимка обязан не ошибиться ни разу, иначе заметка не
 * уедет. Приоритет обратный: уезжает заметка, а снимок это то, что удалось.
 */

/** Потолок текста. 2000, а не 4000: разбор бюджета 16 КБ в разделе 3.4 проекта. */
export const FEEDBACK_TEXT_MAX = 2000;

/** Сколько заметок и сколько байт держит очередь клиента (раздел 6.5). */
export const FEEDBACK_QUEUE_MAX = 20;
export const FEEDBACK_QUEUE_MAX_BYTES = 200_000;

/** Возраст, после которого заметка становится видимой неудачей (раздел 6.5). */
export const FEEDBACK_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

export const CreateFeedbackNoteRequest = z.object({
  /**
   * Ключ идемпотентности от клиента. Повтор отвечает `202`, а не отказом, в
   * отличие от `attempt_id_reused`: там повтор означает поломку клиента, здесь
   * это спроектированный штатный путь очереди (раздел 3.1 проекта).
   */
  noteId: z.string().uuid(),
  /**
   * Границы обязательны: `z.number().int()` пропускает 1e18, `new Date(1e18)`
   * это Invalid Date, и вставка падает пятисоткой вместо отказа на границе.
   * Нижняя это 01.01.2020, верхняя 01.01.2100. Часы клиента врут, и отвергать
   * заметку из-за сбитых часов нельзя, а принимать 2087 год незачем.
   */
  clientTs: z.number().int().min(1_577_836_800_000).max(4_102_444_800_000),
  text: Printable.trim().min(1).max(FEEDBACK_TEXT_MAX),
  /** Уже замаскованный адрес (`maskRoute`). Сырых адресов контракт не принимает. */
  route: Printable.min(1).max(200),
  context: FeedbackContext,
});
export type CreateFeedbackNoteRequest = z.infer<typeof CreateFeedbackNoteRequest>;
